import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Play, ShieldCheck, CheckCircle2, Circle } from "lucide-react";
import { Button } from "../ui/button";
import Reveal from "./Reveal";

const MiniRun = () => {
  const rows = [
    { t: "profile(invoices.csv): 4 rows", done: true },
    { t: "reconcile(invoices.csv, ledger.csv)", done: true },
    { t: "3 matched · 1 missing · 1 amount mismatch", done: true },
    { t: "Auditor tracing each figure", done: false },
  ];
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-xl shadow-primary/5">
      <div className="flex items-center gap-2 border-b border-border pb-3">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <span className="ml-2 text-xs font-medium text-muted-foreground">Example run (illustration)</span>
      </div>
      <div className="space-y-2.5 pt-4">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2.5 text-sm">
            {r.done ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
            ) : (
              <Circle className="h-4 w-4 shrink-0 animate-pulse-dot text-primary/60" />
            )}
            <span className={r.done ? "text-foreground" : "text-muted-foreground"}>{r.t}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between rounded-xl bg-accent px-3.5 py-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-accent-foreground" />
          <span className="text-sm font-semibold text-accent-foreground">Auditor verdict</span>
        </div>
        <span className="rounded-full bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-foreground">VERIFIED</span>
      </div>
    </div>
  );
};

const Hero = () => {
  const navigate = useNavigate();
  return (
    <section id="top" className="relative overflow-hidden pt-28 sm:pt-32">
      <div className="pointer-events-none absolute inset-0 bg-dots opacity-60" />
      <div className="pointer-events-none absolute -top-24 right-0 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -left-20 h-72 w-72 rounded-full bg-emerald-200/40 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 sm:px-8 lg:grid-cols-2 lg:pb-28">
        <div>
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-medium text-muted-foreground shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse-dot" />
              New · An auditor checks every figure against your data
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-6xl">
              An AI teammate that
              <span className="text-primary"> actually does the work</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
              Attach spreadsheets, JSON or PDFs and describe the goal. Nimbus
              runs real data tools on them (profiling, totals, dedupe,
              reconciliation, document search), shows every step, and labels
              each result VERIFIED, PARTIAL or ESTIMATED.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                className="group rounded-full bg-primary px-6 text-base font-semibold shadow-md shadow-primary/20 transition-transform hover:-translate-y-0.5"
                onClick={() => navigate("/workspace")}
              >
                Open the workspace
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-border bg-card px-6 text-base font-semibold hover:bg-secondary"
                onClick={() => document.querySelector("#how")?.scrollIntoView({ behavior: "smooth" })}
              >
                <Play className="mr-1.5 h-4 w-4" />
                See how it works
              </Button>
            </div>
          </Reveal>
        </div>

        <Reveal delay={200} className="relative">
          <div className="animate-floaty">
            <MiniRun />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Hero;
